import { Box, Checkbox, IconButton } from "@mui/material";
import { useGetEcomLatestQuery, useGetMaterialCategoriesQuery, useGetMaterialTypesQuery } from "api/api";
import StyledButton from "components/button/StyledButton";
import FormLabeledTextField from "components/form/FormLabeledTextField";
import FormSelect from "components/form/FormSelect";
import React, { useState } from "react";
import { Controller, set } from "react-hook-form";
import FigmaModal, { ModalType } from "components/modal/FigmaModal";
import { Ecom, MaterialCategories, MaterialTypes } from "types/types";
import ClearIcon from '@mui/icons-material/Clear';

type Structure = 'Foundations' | 'Foundation Walls' | 'Slabs-On-Grade' | 'Slabs/Decks' | 'Beams' | 'Columns' | 'Bracing' | 'Stairs' | 'Exterior Structural Walls' | 'Interior Structural Walls'

interface SmqFieldsProps {
  structure: Structure
  form: any
  fieldArray: any
  projectName: string | null
  materialCategories: MaterialCategories[]
  materialTypes: MaterialTypes[]
  latestEcomData: Ecom[]
}

const SmqFields = ({ structure, form, fieldArray, projectName, materialTypes, materialCategories, latestEcomData }: SmqFieldsProps) => {
  const listMaterialCategories = materialCategories.map(item => item.name);
  const listConcreteMaterialTypes = materialTypes.filter(item => item.category === 'Concrete').map(item => item.name);
  const listSteelReinforcementMaterialTypes = materialTypes.filter(item => item.category === 'Steel Reinforcement').map(item => item.name);
  const listMasonryMaterialTypes = materialTypes.filter(item => item.category === 'Masonry').map(item => item.name);
  const listSteelMaterialTypes = materialTypes.filter(item => item.category === 'Steel').map(item => item.name);
  const listTimberMaterialTypes = materialTypes.filter(item => item.category === 'Timber').map(item => item.name);

  const [activeModal, setActiveModal] = useState<ModalType>('');
  const [factorOverride, setFactorOverride] = useState<Record<number, boolean>>({});
  const [selectedFieldIndex, setSelectedFieldIndex] = useState<number | null>(null);

  const { control, watch, setValue, formState: { errors } } = form;

  const smqErrors = errors?.smqs || [];

  const { fields, append, remove } = fieldArray;

  const classMappings: Record<Structure, string> = {
    'Foundations': 'foundations-row',
    'Foundation Walls': 'foundation-walls-row',
    'Slabs-On-Grade': 'slabs-row',
    'Slabs/Decks': 'decks-row',
    'Beams': 'beams-row',
    'Columns': 'columns-row',
    'Bracing': 'bracing-row',
    'Stairs': 'stairs-row',
    'Exterior Structural Walls': 'exterior-walls-row',
    'Interior Structural Walls': 'interior-walls-row',
  }

  const materialTypeLists = {
    "Concrete": listConcreteMaterialTypes,
    "Steel Reinforcement": listSteelReinforcementMaterialTypes,
    "Masonry": listMasonryMaterialTypes,
    "Steel": listSteelMaterialTypes,
    "Timber": listTimberMaterialTypes,
  }

  const handleCarbonFactorClick = (fieldIndex: number) => {
    if (factorOverride[fieldIndex]) return;
    setSelectedFieldIndex(fieldIndex);
    setActiveModal('updateCarbonFactor');
  }

  const handleModalResponse = (response) => {
    if (response === 'Confirm' && selectedFieldIndex !== null) {
      setFactorOverride(prev => ({ ...prev, [selectedFieldIndex]: true }));
      setSelectedFieldIndex(null);
    }
  }

  const getOverallStructure = () => {
    if ((['Foundations', 'Foundation Walls', 'Slabs-On-Grade'] as Structure[]).includes(structure)) {
      return 'Substructure';
    } else if ((['Slabs/Decks', 'Beams', 'Columns', 'Bracing', 'Stairs', 'Exterior Structural Walls', 'Interior Structural Walls'] as Structure[]).includes(structure)) {
      return 'Superstructure';
    } else {
      console.error("Invalid structure supplied to renderSmqFields, if you've added a new structure, make sure to add it to the getOverallStructure function and classMappings object");
      return ""
    }
  }

  const calculateEmbodiedCarbon = (quantity, carbonFactor) => {
    return carbonFactor ? quantity * carbonFactor : 0;
  }

  const structureClass = classMappings[structure];
  const filteredFields = fields.filter(field => field.field.structure === structure);

  return (
    <React.Fragment>
      <FigmaModal open={!!activeModal} onClose={() => setActiveModal('')} onResponse={handleModalResponse} type={activeModal} />
      {/* Displays structural header regardless of if there are any fields */}
      {filteredFields.length === 0 && (<div className="column-one row-title">{structure}</div>)}
      <div className="smq-row">
        {filteredFields.map((field, index) => {
          const fieldIndex = fields.findIndex(f => f.id === field.id);
          return (
            <React.Fragment key={field.id}>
              <div className="column-one" style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                {index === 0 && <span className="row-title">{structure}</span>}
                <IconButton
                  onClick={() => remove(fieldIndex)}
                  size="small"
                  sx={{
                    position: 'absolute',
                    right: '8px',
                    marginTop: '12px',
                    color: '#555555',
                    '&:hover': {
                      color: '#000000'
                    }
                  }}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </div>
              <div className="column-two">
                <Controller
                  name={`smqs.${fieldIndex}.material_type.category`}
                  control={control}
                  render={({ field }) => (
                    <Box>
                      <FormSelect
                        {...field}
                        label=""
                        error={!!smqErrors[fieldIndex]?.material_type?.category}
                        helperText={smqErrors[fieldIndex]?.material_type?.category?.message}
                        disabled={field.disabled}
                        menuItems={listMaterialCategories}
                        onChange={(e) => {
                          field.onChange(e);
                          setValue(`smqs.${fieldIndex}.material_type.name`, null);
                          setValue(`smqs.${fieldIndex}.quantity`, null);
                          setValue(`smqs.${fieldIndex}.a1_a3_carbon_factor_override`, null);
                          setFactorOverride(prev => ({ ...prev, [fieldIndex]: false }));
                        }}
                      />
                    </Box>
                  )}
                />
              </div>
              <div className="column-three">
                <Controller
                  name={`smqs.${fieldIndex}.material_type.name`}
                  control={control}
                  render={({ field }) => (
                    <Box>
                      <FormSelect
                        {...field}
                        label=""
                        error={!!smqErrors[fieldIndex]?.material_type?.name}
                        helperText={smqErrors[fieldIndex]?.material_type?.name?.message}
                        disabled={field.disabled}
                        menuItems={materialTypeLists[watch(`smqs.${fieldIndex}.material_type.category`)] || []}
                        onChange={(value) => {
                          field.onChange(value)
                          setValue(`smqs.${fieldIndex}.material_type.unit`, materialTypes.find(type => type.name === watch(`smqs.${fieldIndex}.material_type.name`))?.unit || "");
                          setValue(`smqs.${fieldIndex}.a1_a3_carbon_factor_override`, null);
                          setFactorOverride(prev => ({ ...prev, [fieldIndex]: false }));
                        }}
                      />
                    </Box>
                  )}
                />
              </div>
              <div className="column-four">
                <Controller
                  name={`smqs.${fieldIndex}.quantity`}
                  control={control}
                  render={({ field }) => (
                    <Box sx={{ marginBottom: '3px' }}>
                      <FormLabeledTextField
                        {...field}
                        label={materialTypes.find(type => type.name === watch(`smqs.${fieldIndex}.material_type.name`))?.unit || ""}
                        error={!!smqErrors[fieldIndex]?.quantity}
                        helperText={smqErrors[fieldIndex]?.quantity?.message}
                        disabled={field.disabled}
                        updateFunction={(value) => field.onChange(parseFloat(value))}
                      />
                    </Box>
                  )}
                />
              </div>
              <div className="column-five">
                <Controller
                  name={`smqs.${fieldIndex}.a1_a3_carbon_factor_override`}
                  control={control}
                  defaultValue={null}
                  render={({ field }) => {
                    const displayValue = field.value ?? (latestEcomData['ECOMData']?.find(item => item.material_type === watch(`smqs.${fieldIndex}.material_type.name`))?.a1_a3_carbon_factor || null);
                    return (
                      <Box onClick={() => handleCarbonFactorClick(fieldIndex)} sx={{ marginBottom: '3px' }}>
                        <FormLabeledTextField
                          {...field}
                          label={latestEcomData['ECOMData']?.find(ecomItem => ecomItem.material_type === watch(`smqs.${fieldIndex}.material_type.name`))?.a1_a3_carbon_factor_unit}
                          value={displayValue}
                          disabled={!factorOverride[fieldIndex]}
                          error={!!smqErrors[fieldIndex]?.a1_a3_carbon_factor_override}
                          helperText={smqErrors[fieldIndex]?.a1_a3_carbon_factor_override?.message}
                          onClick={() => handleCarbonFactorClick(fieldIndex)}
                          updateFunction={(value) => field.onChange(parseFloat(value))}
                        />
                      </Box>
                    )
                  }}
                />
              </div>
              <div className="column-six">
                <Controller
                  name={`smqs.${fieldIndex}.is_lateral`}
                  control={control}
                  render={({ field }) => (
                    <Box sx={{ height: '40px', marginTop: '14px' }}>
                      <Checkbox
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    </Box>
                  )}
                />
              </div>
              <div className="column-seven">
                <Box sx={{ height: '40px', marginTop: '24px', color: 'black' }}>
                  {parseInt(calculateEmbodiedCarbon(
                    watch(`smqs.${fieldIndex}.quantity`) || 0,
                    latestEcomData['ECOMData']?.find(ecomItem => ecomItem.material_type === watch(`smqs.${fieldIndex}.material_type.name`))?.a1_a3_carbon_factor || 0
                  ).toFixed()).toLocaleString()} kgCO2e
                </Box>
              </div>
            </React.Fragment>
          );
        })}
      </div>

      <Box className="column-two" sx={{ position: "sticky", marginTop: '12px' }}>
        <StyledButton
          label="+ Add Material"
          type="outlined"
          onClick={() => append({
            field: { structure: structure, overall_structure: getOverallStructure() },
            material_type: { category: '', name: null, unit: null },
            quantity: null,
            is_lateral: false,
            a1_a3_carbon_factor_override: null,
            ecom_ref: '',
            project: projectName
          })}
        />
      </Box>
    </React.Fragment>
  );
};

export default SmqFields;